import React from 'react';

import {
  AppstoreTwoTone,
  ExportOutlined,
  ImportOutlined,
  ProfileTwoTone,
  WarningTwoTone,
} from '@ant-design/icons';

import { theme as antTheme, Badge, Layout, Row, Space, Tooltip } from 'antd';

import { useTranslation } from 'react-i18next';

import { useNavigate, useParams } from 'react-router-dom';

import { NSKeys } from '@app/i18n';
import { hasHistory } from '@app/services/hasHistory';
import { useLocalSelector } from '@app/services/hooks/useRedux';
import { selectIsCounterpartySpace } from '@app/store/currentSessionReducer';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { HeaderTitle } from '@components/HeaderTitle';
import { DeactivatedTag } from '@components/warehouses';

import { useGetWarehouseQuery, useGetWarehouseSkusImportErrorCountQuery } from '../../hooks';

import { useWarehouse } from './WarehouseProvider';
import { SettingsControl } from './components/SettingsControl';
import { TABS } from './interfaces';
import { Acceptance } from './pages/Acceptance';
import { ImportErrors } from './pages/ImportErrors';
import { Nomenclature } from './pages/Nomenclature';
import { Settings } from './pages/Settings';
import { Shipments } from './pages/Shipments';
import { Container, Tabs as StyledTabs } from './styles';

export const Single: React.FC = () => {
  const navigate = useNavigate();
  const { activeTab, setActiveTab } = useWarehouse();
  const { token } = antTheme.useToken();
  const params = useParams<{ id: string }>();
  const warehouseT = useTranslation(NSKeys.warehouse);
  const isCounterparty = useLocalSelector(selectIsCounterpartySpace);

  const { data, isLoading } = useGetWarehouseQuery(params?.id || '');
  const { data: importErrorsCount } = useGetWarehouseSkusImportErrorCountQuery(params?.id || '');
  const isImportErrorsTabDisabled = (importErrorsCount?.count ?? 0) === 0;

  const goBack = () => (hasHistory() ? navigate(-1) : navigate('/dashboard/warehouses'));

  return (
    <Layout>
      <Layout.Header style={{ border: 0 }}>
        <Breadcrumbs apiTitles={[{ id: ':warehouseId', value: data?.name }]} />
        <Row justify="space-between" align="middle">
          <HeaderTitle loading={isLoading} title={`${data?.name || params.id}`} onClick={goBack}>
            {data?.deactivated_at && <DeactivatedTag deactivatedAt={data.deactivated_at} byCustomer={isCounterparty} />}
          </HeaderTitle>
          {activeTab === TABS.SETTINGS && <SettingsControl />}
        </Row>
      </Layout.Header>
      <Layout.Content>
        <StyledTabs
          destroyInactiveTabPane={true}
          activeKey={activeTab}
          onChange={(tab) => setActiveTab(tab as TABS)}
          items={[
            {
              key: TABS.SETTINGS,
              label: (
                <Space>
                  <ProfileTwoTone twoToneColor={token?.colorPrimary} />
                  {warehouseT.t('tabSettings')}
                </Space>
              ),
              children: (
                <Container>
                  <Settings />
                </Container>
              ),
            },
            {
              key: TABS.NOMENCLATURE,
              label: (
                <Space>
                  <AppstoreTwoTone twoToneColor={token?.colorPrimary} />
                  {warehouseT.t('tabNomenclature')}
                </Space>
              ),
              children: (
                <Container>
                  <Nomenclature />
                </Container>
              ),
            },
            {
              key: TABS.SHIPMENTS,
              label: (
                <Space>
                  <ExportOutlined style={{ color: token?.colorPrimary }} />
                  {warehouseT.t('tabShipments')}
                </Space>
              ),
              children: (
                <Container>
                  <Shipments />
                </Container>
              ),
            },
            {
              key: TABS.ACCEPTANCE,
              label: (
                <Space>
                  <ImportOutlined style={{ color: token?.colorPrimary }} />
                  {warehouseT.t('tabAcceptance')}
                </Space>
              ),
              children: (
                <Container>
                  <Acceptance />
                </Container>
              ),
            },
            {
              key: TABS.IMPORT_ERRORS,
              disabled: isImportErrorsTabDisabled,
              label: (
                <Tooltip title={isImportErrorsTabDisabled ? 'Список ошибок пуст' : undefined}>
                  <Space>
                    <WarningTwoTone twoToneColor={token?.colorPrimary} />
                    <Badge count={importErrorsCount?.count ?? 0} size="small">
                      {warehouseT.t('tabImportErrors')}
                    </Badge>
                  </Space>
                </Tooltip>
              ),
              children: (
                <Container>
                  <ImportErrors />
                </Container>
              ),
            },
          ]}
          $colorBgBase={token.colorBgBase}
        />
      </Layout.Content>
    </Layout>
  );
};
